import type { ApiResponse } from "../../../types/shared";
import httpClient from "../../../utils/httpClient";
import type {
  ContentStrategy,
  ContentStrategyWithDetails,
  CreateContentStrategyParams,
  UpdateContentStrategyParams,
  DeleteContentStrategyParams,
  GetContentParams,
  ContentListResponse,
  GenerateByStrategyParams,
  GenerateByStrategyResponse,
} from "../interfaces";

/** Timeout for long-running generate-by-strategy (video generation: 4–10 min). */
const GENERATE_TIMEOUT_MS = 11 * 60 * 1000;

interface IContentService {
  createContentStrategy(
    params: CreateContentStrategyParams
  ): Promise<ContentStrategyWithDetails>;
  getAllContentStrategies(): Promise<ContentStrategy[]>;
  getContentStrategyById(id: number): Promise<ContentStrategyWithDetails>;
  updateContentStrategy(
    params: UpdateContentStrategyParams
  ): Promise<ContentStrategyWithDetails>;
  deleteContentStrategy(
    params: DeleteContentStrategyParams
  ): Promise<{ message: string; id: number }>;
  getContent(params?: GetContentParams): Promise<ContentListResponse>;
  generateByStrategy(
    params: GenerateByStrategyParams
  ): Promise<GenerateByStrategyResponse>;
}

class ContentService implements IContentService {
  private base = "/v1/content/strategies";
  private contentBase = "/v1/content";

  createContentStrategy = async (
    params: CreateContentStrategyParams
  ): Promise<ContentStrategyWithDetails> => {
    const res = await httpClient.post<ApiResponse<ContentStrategyWithDetails>>(
      this.base,
      {
        name: params.name,
        ...(params.description != null && params.description !== "" && { description: params.description }),
        status: params.status,
        mode: params.mode,
        config: params.config,
        ...(params.brandIds != null && { brandIds: params.brandIds }),
        ...(params.campaignIds != null && { campaignIds: params.campaignIds }),
        ...(params.agentIds != null && { agentIds: params.agentIds }),
        ...(params.integrationIds != null && { integrationIds: params.integrationIds }),
      }
    );

    return res.data.data;
  };

  getAllContentStrategies = async (): Promise<ContentStrategy[]> => {
    const res = await httpClient.get<ApiResponse<ContentStrategy[]>>(
      this.base
    );

    return res.data.data;
  };

  getContentStrategyById = async (
    id: number
  ): Promise<ContentStrategyWithDetails> => {
    const res = await httpClient.get<ApiResponse<ContentStrategyWithDetails>>(
      `${this.base}/${id}`
    );

    return res.data.data;
  };

  updateContentStrategy = async (
    params: UpdateContentStrategyParams
  ): Promise<ContentStrategyWithDetails> => {
    const { id, ...body } = params;

    const res = await httpClient.put<ApiResponse<ContentStrategyWithDetails>>(
      `${this.base}/${id}`,
      {
        ...(body.name != null && { name: body.name }),
        ...(body.description !== undefined && { description: body.description }),
        ...(body.status != null && { status: body.status }),
        ...(body.mode != null && { mode: body.mode }),
        ...(body.config != null && { config: body.config }),
        ...(body.brandIds != null && { brandIds: body.brandIds }),
        ...(body.campaignIds != null && { campaignIds: body.campaignIds }),
        ...(body.agentIds != null && { agentIds: body.agentIds }),
        ...(body.integrationIds != null && { integrationIds: body.integrationIds }),
      }
    );

    return res.data.data;
  };

  deleteContentStrategy = async (
    params: DeleteContentStrategyParams
  ): Promise<{ message: string; id: number }> => {
    const res = await httpClient.delete<
      ApiResponse<{ message: string; id: number }>
    >(`${this.base}/${params.id}`);

    return res.data.data;
  };

  getContent = async (
    params?: GetContentParams
  ): Promise<ContentListResponse> => {
    const res = await httpClient.get<ApiResponse<ContentListResponse>>(
      this.contentBase,
      { params }
    );
    return res.data.data;
  };

  generateByStrategy = async (
    params: GenerateByStrategyParams
  ): Promise<GenerateByStrategyResponse> => {
    const res = await httpClient.post<
      ApiResponse<GenerateByStrategyResponse>
    >(`${this.contentBase}/generate-by-strategy`, params, {
      timeout: GENERATE_TIMEOUT_MS,
    });
    return res.data.data;
  };
}

export const contentService = new ContentService();
